/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';

import { datadogRum } from '@datadog/browser-rum';
import { Honeybadger } from '@honeybadger-io/react';
import { shutdown } from '@intercom/messenger-js-sdk';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { requestApi } from 'api/Api';

import { clearSelfSchedulingPersistence } from 'lib/helpers/draftCrypto';
import { clearStoragePreserveKiosk, isKioskDevice } from 'lib/helpers/kiosk';

import Modal from 'components/shared/Modal/Modal';
import { current_admin } from 'components/admin/lib/adminState';
import { hasBeenAuthChecked, userState } from 'components/state';
import NotFound from 'components/practice/Dashboard/components/NotFound';
import {
  currentPractice,
  ownerPractices,
  ownerSelectedLocation
} from 'components/practice/practiceState';
import { isAmbientListeningActive } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/AmbientListening/state';

const AutoLogout = () => {
  const [user, setUser] = useRecoilState(userState);
  const [adminState, setAdminState] = useRecoilState(current_admin);

  const setPractice = useSetRecoilState(currentPractice);
  const [remaining, setRemaining] = useState(5 * 60);
  const [showModal, setShowModal] = useState(false);
  const setHasBeenAuthChecked = useSetRecoilState(hasBeenAuthChecked);
  const setOwnerSelectedLocation = useSetRecoilState(ownerSelectedLocation);
  const setOwnerPractices = useSetRecoilState(ownerPractices);
  const isAmbientListeningInUse = useRecoilValue(isAmbientListeningActive);
  const navigate = useNavigate();

  const logout = async () => {
    try {
      const isAdmin = adminState?.logged_in;
      const logoutUrl = isAdmin ? '/api/admin/logout' : '/api/user/logout';

      try {
        await requestApi({ url: logoutUrl, navigate });
      } catch (e) {
        console.error(e);
      }

      sessionStorage.removeItem('myriad-session-reference-id');
      shutdown(); // intercom

      setUser({
        user_id: '',
        f_name: '',
        l_name: '',
        email: '',
        state: '',
        kind: '',
        logged_in: false
      });

      if (isAdmin) {
        setAdminState({
          user_id: '',
          f_name: '',
          l_name: '',
          email: '',
          state: '',
          role: '',
          logged_in: false
        });
      }

      datadogRum.clearUser();

      setOwnerPractices([]);
      setOwnerSelectedLocation(null);
      setHasBeenAuthChecked({ checked: false, isImpersonate: false });
      setPractice({ id: null, name: '', email: '', timezone: '', display_settings: null });

      const isKiosk = isKioskDevice();
      clearStoragePreserveKiosk();
      // Also clear the self-scheduling IDB key. localStorage.clear() misses IndexedDB.
      await clearSelfSchedulingPersistence({ onError: (e) => Honeybadger.notify(e) });
      localStorage.setItem('logout-event', `logout-${new Date().toISOString()}`);

      if (isKiosk) {
        navigate('/kiosk/idle');
        return;
      }

      const path = isAdmin ? '/admin/login' : '/login';
      navigate(path, { state: { idle: true } });
    } catch (error) {
      clearStoragePreserveKiosk();
      Honeybadger.notify(
        `file: AutoLogout, method: logout - catch, error: ${error ?? "There's been an error"}`
      );
    }
  };

  const { reset, isLastActiveTab, isIdle } = useIdleTimer({
    timeout: 10 * 60 * 1000,
    onIdle: () => {
      if (!isLastActiveTab()) return;
      setShowModal(true);
      setRemaining(5 * 60);
    },
    debounce: 500,
    crossTab: true,
    syncTimers: 200,
    disabled: !!isAmbientListeningInUse // ⛔ Prevent auto-logout trigger
  });

  useEffect(() => {
    let interval;
    if (showModal) {
      interval = setInterval(() => {
        // If user became active again (locally or in another tab), dismiss the modal
        if (!isIdle() || !isLastActiveTab()) {
          setShowModal(false);
          reset();
          return;
        }

        setRemaining((prevRemaining) => {
          if (prevRemaining <= 1) {
            clearInterval(interval);
            datadogRum.addAction('idle-timeout-logout', {
              file: 'AutoLogout',
              method: 'idle-timeout',
              userId: user?.user_id || 'unknown',
              adminId: adminState?.user_id || 'unknown'
            });
            logout();
            return 0;
          }
          return prevRemaining - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [showModal]);

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === 'logout-event' && (user?.logged_in || adminState?.logged_in)) {
        logout();
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [user.logged_in, adminState?.logged_in]);

  useEffect(() => {
    const checkSessionValid = async () => {
      try {
        const resData = await requestApi({
          url: '/api/user/check_token',
          navigate
        });
        if (
          resData &&
          resData.token_valid === false &&
          (user?.logged_in || adminState?.logged_in)
        ) {
          datadogRum.addAction('session-validation-logout', {
            file: 'AutoLogout',
            method: 'session-validation',
            userId: user?.user_id || 'unknown',
            adminId: adminState?.user_id || 'unknown',
            tokenValid: resData?.token_valid
          });
          logout();
        }
      } catch (error) {
        Honeybadger.notify(
          `file: AutoLogout, method: checkSessionValid - catch, error: ${error ?? "There's been an error"}`
        );
        console.error('Failed to check session heartbeat.', error);
      }
    };

    const interval = setInterval(
      () => {
        const currentPath = location?.pathname || '';

        if (
          (user?.logged_in || adminState?.logged_in) &&
          !currentPath.includes('/login') &&
          !currentPath.includes('/admin/login')
        ) {
          checkSessionValid();
        }
      },
      10 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, [user?.logged_in, adminState?.logged_in]);

  useEffect(() => {
    const currentPath = location?.pathname || '';
    const isLoginPage = currentPath.includes('/login') || currentPath.includes('/admin/login');

    if (isLoginPage && showModal) {
      setShowModal(false);
    }
  }, [location?.pathname, showModal]);

  if (!user?.logged_in && !adminState?.logged_in) return null;

  return (
    <Modal
      handleOpen={showModal}
      disableHeader={true}
      shouldCloseOnOverlayClick={false}
      overlayClassName="z-[300]"
      isFooter={false}
      isCentered>
      <div className="my-10">
        <NotFound
          icon="illustration-automatization"
          title="Are you still there?"
          description={`You will be logged out in ${Math.floor(remaining / 60)}:${
            remaining % 60 < 10 ? `0${remaining % 60}` : remaining % 60
          } minutes due to inactivity.`}
          className="pt-0"
        />
      </div>
    </Modal>
  );
};
export default AutoLogout;
